import { Fragment } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AccountsSection } from "@/components/account/accounts-section";
import { AuthorizationCodeSection } from "@/components/account/authorization-code-section";
import { ContactSection } from "@/components/account/contact-section";
import { LanguageSection } from "@/components/account/language-section";
import { LinkedCompaniesSection } from "@/components/account/linked-companies-section";
import { ProvidedBy } from "@/components/shell/provided-by";
import { Button, ContactBlock, EmptyState, PageSkeleton, PageTitle } from "@/components/ui";
import { useActiveMembership, useMyCustomerContext } from "@/hooks/use-customer-context";
import { signOut } from "@/lib/auth";
import { brandingFromMembership } from "@/lib/portal-memory";

export const Route = createFileRoute("/_customer/account/")({
  component: AccountPage,
});

const PAGE = "flex flex-col gap-5 lg:gap-6";

function AccountPage() {
  const { t } = useTranslation("customer");
  const contextQuery = useMyCustomerContext();
  const active = useActiveMembership();

  if (contextQuery.isLoading) {
    return <PageSkeleton />;
  }

  const memberships = contextQuery.data?.memberships ?? [];

  return (
    <div className={PAGE}>
      <PageTitle title={t("account.title")} />

      {active ? (
        <>
          <ContactSection membership={active} />
          <AccountsSection memberships={memberships} active={active} />
          {/* The membership flag is the utility's switch AND an open account;
              keyed by account so a switch starts both sections over. */}
          {active.account_codes_enabled ? (
            <Fragment key={active.account_id}>
              <LinkedCompaniesSection
                accountId={active.account_id}
                timezone={active.org_timezone}
                orgName={active.org_name}
              />
              <AuthorizationCodeSection
                accountId={active.account_id}
                timezone={active.org_timezone}
                orgName={active.org_name}
              />
            </Fragment>
          ) : null}
          <LanguageSection />
          <ContactBlock branding={brandingFromMembership(active)} />
        </>
      ) : (
        <EmptyState title={t("accessDenied.title")} description={t("accessDenied.body")} />
      )}

      <div>
        <Button variant="secondary" onClick={() => signOut({ revoke: true })}>
          {t("shell.signOut")}
        </Button>
      </div>
      <ProvidedBy />
    </div>
  );
}
